import {
  dashboardPageStackClassName,
  dashboardSectionCardClassName,
  dashboardSectionCardContentClassName,
  dashboardSectionCardHeaderClassName,
} from "@/modules/dashboard/presentation/ui/dashboard-card-styles";
import {
  ReportSummaryStatCard,
  ReportSummaryStatGrid,
  reportSummaryCount,
} from "@/modules/reports/presentation/ui/ReportSummaryStatCard";
import { DashboardChartEmptyState } from "@/modules/dashboard/presentation/ui/DashboardQuickActionsCard";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
  Skeleton,
} from "@devhop/ui";
import { useSearch } from "@tanstack/react-router";
import {
  BanknoteIcon,
  CalendarIcon,
  FileTextIcon,
  TrendingUpIcon,
  WalletIcon,
} from "lucide-react";
import { useMemo } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { formatDateLocal } from "@/shared/lib/date-time-performance";
import { AreaChart, type AreaChartData } from "@/shared/ui/AreaChart";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { CompactMoneyAmount } from "@/shared/ui/CompactMoneyAmount";
import type { ChartConfig } from "@/shared/ui/ChartContainer";
import { usePerformanceReportSummaryQuery } from "../api/queries";
import {
  PerformanceReportDateFilter,
  type PerformanceReportSearch,
  usePerformanceReportDates,
} from "../ui/PerformanceReportDateFilter";
import {
  formatCurrencyBuckets,
  PerformanceReportsNav,
  summaryStatCurrencyBucketOptions,
} from "../ui/PerformanceReportsNav";

const currencyBucketValueClassName = summaryStatCurrencyBucketOptions.className;

export function PerformanceReportsDashboardPage() {
  const search = useSearch({
    from: "/app/performance/reports",
  }) as PerformanceReportSearch;

  const { dateFrom, dateTo, groupBy } = usePerformanceReportDates(search);

  const query = useMemo(
    () => ({ dateFrom, dateTo, groupBy }),
    [dateFrom, dateTo, groupBy],
  );

  const { data, isLoading } = usePerformanceReportSummaryQuery(query);

  const areaChartData: AreaChartData[] = useMemo(
    () =>
      (data?.trend ?? []).map((item) => ({
        period: formatDateLocal(item.period),
        revenue: item.revenue.LAK,
        payoutPaid: item.payoutPaid,
      })),
    [data?.trend],
  );

  const areaChartConfig: ChartConfig = {
    revenue: {
      label: "ລາຍຮັບ LAK (ໃບຮັບເງິນ)",
      color: "var(--chart-1)",
    },
    payoutPaid: {
      label: "ຈ່າຍນັກສະແດງແລ້ວ",
      color: "var(--chart-2)",
    },
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/performance/dashboard" },
            { label: "ລາຍງານງານສະແດງ" },
          ]}
        />
      </Header>

      <Main>
        <div className={dashboardPageStackClassName}>
          <section>
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0 space-y-4">
                <h1 className="font-semibold text-2xl tracking-tight">
                  ລາຍງານງານສະແດງ
                </h1>
                <PerformanceReportsNav />
              </div>
              <div className="w-full shrink-0 lg:max-w-xl">
                <PerformanceReportDateFilter
                  routeFrom="/app/performance/reports"
                  showGroupBy
                />
              </div>
            </div>
          </section>

          <ReportSummaryStatGrid columns={4} className="mb-0">
            <ReportSummaryStatCard
              title="ງານສະແດງ"
              icon={CalendarIcon}
              tone="packages"
              isLoading={isLoading}
              value={reportSummaryCount(data?.events.total ?? 0)}
            />
            <ReportSummaryStatCard
              title="ໃບສະເໜີລາຄາ / ໃບເກັບເງິນ"
              icon={FileTextIcon}
              tone="attendance"
              isLoading={isLoading}
              value={formatCurrencyBuckets(data?.quoted, summaryStatCurrencyBucketOptions)}
            />
            <ReportSummaryStatCard
              title="ລາຍຮັບ / ຄ້າງເກັບ"
              icon={BanknoteIcon}
              tone="revenue"
              isLoading={isLoading}
              value={formatCurrencyBuckets(
                data?.revenueCollected,
                summaryStatCurrencyBucketOptions,
              )}
            />
            <ReportSummaryStatCard
              title="ຈ່າຍ / ຄ້າງຈ່າຍ"
              icon={WalletIcon}
              tone="students"
              isLoading={isLoading}
              value={
                <div className={cn("space-y-1.5", currencyBucketValueClassName)}>
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    <span className="text-muted-foreground text-xs">ຈ່າຍແລ້ວ</span>
                    <CompactMoneyAmount
                      amount={data?.payoutPaid ?? 0}
                      currencyClassName="font-medium text-base sm:text-lg"
                    />
                  </div>
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    <span className="text-muted-foreground text-xs">ຄ້າງຈ່າຍ</span>
                    <CompactMoneyAmount
                      amount={data?.payoutOutstanding ?? 0}
                      currencyClassName="font-medium text-base sm:text-lg"
                    />
                  </div>
                </div>
              }
            />
          </ReportSummaryStatGrid>

          <Card className={dashboardSectionCardClassName}>
            <CardHeader className={dashboardSectionCardHeaderClassName}>
              <CardTitle className="flex items-center gap-2 text-base">
                <TrendingUpIcon className="size-4" aria-hidden />
                ແນວໂນ້ມລາຍຮັບ ແລະ ການຈ່າຍ
              </CardTitle>
            </CardHeader>
            <CardContent className={dashboardSectionCardContentClassName}>
              {isLoading ? (
                <Skeleton className="aspect-video max-h-[320px] w-full rounded-lg" />
              ) : areaChartData.length > 0 ? (
                <AreaChart
                  data={areaChartData}
                  config={areaChartConfig}
                  xDataKey="period"
                  valueDataKey={["revenue", "payoutPaid"]}
                  xAxisTickFormatter={(v) =>
                    typeof v === "string" ? v.slice(0, 10) : String(v)
                  }
                  className="mx-auto aspect-video max-h-[320px]"
                />
              ) : (
                <DashboardChartEmptyState className="aspect-video max-h-[320px]" />
              )}
            </CardContent>
          </Card>
        </div>
      </Main>
    </>
  );
}
